"use client";

import type React from "react";
import { LogOut, Menu, User, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import type { StartClipping } from "../landing/HeroSection";

export type AppNavigationPage = "create" | "clips" | "account";

interface NavbarProps {
  onStartClipping?: StartClipping;
  onShowPricing?: () => void;
  onShowDashboard?: () => void;
  onShowClips?: () => void;
  onGoHome?: () => void;
  onSignIn?: () => void;
  onSignUp?: () => void;
  activeAppPage?: AppNavigationPage | null;
}

const accountLabel = (user: any): string => {
  const name =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    "Account";
  const firstName = String(name).trim().split(/\s+/)[0] || "Account";
  return firstName.length > 13 ? `${firstName.slice(0, 12)}...` : firstName;
};

export const Navbar: React.FC<NavbarProps> = ({
  onStartClipping,
  onShowPricing,
  onShowDashboard,
  onShowClips,
  onGoHome,
  onSignIn,
  activeAppPage = null,
}) => {
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isAppPage = activeAppPage !== null;

  const closeMenu = () => setIsMobileMenuOpen(false);
  const start = () => {
    closeMenu();
    void onStartClipping?.();
  };
  const showClips = () => {
    closeMenu();
    onShowClips?.();
  };
  const showDashboard = () => {
    closeMenu();
    onShowDashboard?.();
  };
  const appItemClassName = (
    page: AppNavigationPage,
    mobile = false
  ): string => {
    const layout = mobile
      ? "block min-h-11 w-full text-left"
      : "";
    const state =
      activeAppPage === page
        ? "bg-blue-50 font-bold text-blue-700"
        : "font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700";

    return `${layout} rounded-xl px-4 py-3 transition ${state}`.trim();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/70 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-[4.75rem] max-w-[90rem] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onGoHome}
          aria-label="TopClip home"
          className="flex min-h-11 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <img
            src="/final-logo-icon.png"
            alt="TopClip"
            className="h-6 w-auto sm:h-7"
          />
        </button>

        <nav
          className="hidden items-center gap-1 xl:flex"
          aria-label="Primary navigation"
        >
          {isAppPage && user ? (
            <>
              <button
                type="button"
                onClick={start}
                aria-current={activeAppPage === "create" ? "page" : undefined}
                className={appItemClassName("create")}
              >
                Create
              </button>
              <button
                type="button"
                onClick={showClips}
                aria-current={activeAppPage === "clips" ? "page" : undefined}
                className={appItemClassName("clips")}
              >
                My Clips
              </button>
              <button
                type="button"
                onClick={onShowDashboard}
                aria-current={activeAppPage === "account" ? "page" : undefined}
                className={appItemClassName("account")}
              >
                Account
              </button>
            </>
          ) : (
            <>
              <Link className="rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700" to="/#examples">
                Examples
              </Link>
              <Link className="rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700" to="/#how-it-works">
                How It Works
              </Link>
              <Link
                className="rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                to="/#pricing-section"
                onClick={(event) => {
                  if (onShowPricing && !event.metaKey && !event.ctrlKey) {
                    event.preventDefault();
                    onShowPricing();
                  }
                }}
              >
                Pricing
              </Link>
              {user && (
                <>
                  <button
                    type="button"
                    onClick={showClips}
                    className="rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                  >
                    My Clips
                  </button>
                  <button
                    type="button"
                    onClick={onShowDashboard}
                    className="rounded-xl px-4 py-3 font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                  >
                    Account
                  </button>
                </>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <button
                type="button"
                onClick={onShowDashboard}
                aria-label={isAppPage ? `Account: ${accountLabel(user)}` : undefined}
                className="hidden min-h-11 items-center gap-2 rounded-xl px-2.5 text-[0.9375rem] font-semibold text-slate-700 sm:flex"
              >
                {user?.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-blue-600 text-white">
                    <User aria-hidden="true" className="h-4 w-4" />
                  </span>
                )}
                <span>{accountLabel(user)}</span>
              </button>
              {!isAppPage && (
                <button
                  type="button"
                  onClick={start}
                  className="hidden min-h-11 rounded-xl bg-[#1677ff] px-5 font-bold text-white transition hover:bg-[#0868ea] sm:inline-flex sm:items-center"
                >
                  Create Clips
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onSignIn}
                className="hidden min-h-11 rounded-xl px-3 font-semibold text-slate-700 transition hover:bg-slate-100 sm:inline-flex sm:items-center"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={start}
                className="inline-flex min-h-11 items-center rounded-xl bg-[#1677ff] px-3.5 text-sm font-bold text-white transition hover:bg-[#0868ea] sm:px-5 sm:text-base"
              >
                Get started
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
            className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 text-slate-800 xl:hidden"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <nav
          aria-label="Mobile navigation"
          className="absolute inset-x-4 top-[4.55rem] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl xl:hidden"
        >
          {(!isAppPage || !user) &&
            [
              ["Examples", "/#examples"],
              ["How It Works", "/#how-it-works"],
              ["Pricing", "/#pricing-section"],
            ].map(([name, to]) => (
              <Link
                key={name}
                to={to}
                onClick={closeMenu}
                className="block min-h-11 rounded-xl px-4 py-3 font-semibold text-slate-800 hover:bg-blue-50"
              >
                {name}
              </Link>
            ))}
          {user ? (
            <>
              {isAppPage ? (
                <>
                  <button type="button" onClick={start} aria-current={activeAppPage === "create" ? "page" : undefined} className={appItemClassName("create", true)}>
                    Create
                  </button>
                  <button type="button" onClick={showClips} aria-current={activeAppPage === "clips" ? "page" : undefined} className={appItemClassName("clips", true)}>
                    My Clips
                  </button>
                  <button type="button" onClick={showDashboard} aria-current={activeAppPage === "account" ? "page" : undefined} className={appItemClassName("account", true)}>
                    Account
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={showClips} className="block min-h-11 w-full rounded-xl px-4 py-3 text-left font-semibold text-slate-800 hover:bg-blue-50">
                    My Clips
                  </button>
                  <button type="button" onClick={() => { closeMenu(); onShowDashboard?.(); }} className="block min-h-11 w-full rounded-xl px-4 py-3 text-left font-semibold text-slate-800 hover:bg-blue-50">
                    Account
                  </button>
                  <button type="button" onClick={start} className="mt-2 block min-h-11 w-full rounded-xl bg-[#1677ff] px-4 py-3 text-left font-bold text-white hover:bg-[#0868ea]">
                    Create Clips
                  </button>
                </>
              )}
              <button type="button" onClick={async () => { closeMenu(); await signOut(); }} className="mt-2 flex min-h-11 w-full items-center gap-2 rounded-xl px-4 py-3 text-left font-semibold text-red-600 hover:bg-red-50">
                <LogOut aria-hidden="true" className="h-4 w-4" />
                Sign out
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => { closeMenu(); onSignIn?.(); }} className="block min-h-11 w-full rounded-xl px-4 py-3 text-left font-semibold text-slate-800 hover:bg-blue-50 sm:hidden">
                Sign in
              </button>
              <button type="button" onClick={start} className="mt-2 block min-h-11 w-full rounded-xl bg-[var(--tc-blue)] px-4 py-3 text-left font-bold text-white">
                Get started
              </button>
            </>
          )}
        </nav>
      )}
    </header>
  );
};
